/* eslint-disable max-lines */
import { USE_RECORDING_V2 } from '@/components/constants/flags';
import * as chunkAudio from '@/lib/chunkAudioActions';
import { sleep } from '@/lib/utils';
import { store } from '@/store';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { createModel } from '@rematch/core';
import { max } from 'lodash-es';
import { isSafari } from 'react-device-detect';
import {
  AudioContext,
  type IAnalyserNode,
  type IAudioContext,
  type IMediaStreamAudioDestinationNode,
  type IMediaStreamAudioSourceNode,
  MediaStreamAudioDestinationNode,
  type MediaStreamAudioSourceNode,
} from 'standardized-audio-context';
import { type RootModel } from '.';
import { USE_CHUNK_RECORDING } from './scribeRecordModel';

// import offlineAudioRecorder from '@/utils/offlineAudioRecorder';
// import { RecordingStatus } from '../scribeModel';

// todo use tanstack's states

export type Mp3OutputSampleRates =
  | 8000
  | 11025
  | 12000
  | 16000
  | 22050
  | 24000
  | 32000
  | 44100
  | 48000;

export const HIGHEST_QUALITY_SAMPLE_RATE = 48000; // bitrate: 192 kb/s

export type VolumeDetectLevel = 'normal' | 'low' | 'noSound';

export type InitialState = {
  audioSourceDevices: MediaDeviceInfo[];
  selectedAudioSource?: MediaDeviceInfo;
  volumeDetectLevel: VolumeDetectLevel;
  meterVolume: number;

  audioStream?: MediaStream;
  audioSource?: IMediaStreamAudioSourceNode<IAudioContext>;

  // used by not chunk recording flag
  audioContext: AudioContext | null;
  // used by chunk flag
  mediaStreamAudioDestinationNode: IMediaStreamAudioDestinationNode<IAudioContext> | null;
  mediaStreamAudioSourceNode?: IMediaStreamAudioSourceNode<IAudioContext>;

  analyser?: IAnalyserNode<IAudioContext>;

  volumeInterval?: ReturnType<typeof setInterval>;
  volumeLevelInterval?: ReturnType<typeof setInterval>;

  isDetectingVolume?: boolean;

  encodeSampleRate: Mp3OutputSampleRates;
};

let audioContext: IAudioContext | null = null;
if (typeof window !== 'undefined') {
  try {
    audioContext = new AudioContext();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    // logger.error(_);
  }
}

const initialState: InitialState = {
  audioSourceDevices: [],
  volumeDetectLevel: 'normal',
  meterVolume: 0,

  audioContext: audioContext,
  mediaStreamAudioDestinationNode: audioContext
    ? new MediaStreamAudioDestinationNode(audioContext)
    : null,

  // wait for GCP's audio suggestions to be ready
  encodeSampleRate: HIGHEST_QUALITY_SAMPLE_RATE,
};

export const audio = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setEncodeSampleRate(state, payload: Mp3OutputSampleRates) {
      return {
        ...state,
        encodeSampleRate: payload,
      };
    },

    setMediaStreamAudioSourceNode(
      state,
      payload: MediaStreamAudioSourceNode<IAudioContext> | undefined
    ) {
      return { ...state, mediaStreamAudioSourceNode: payload };
    },

    setAudioSourceDevices(state, payload: MediaDeviceInfo[]) {
      return { ...state, audioSourceDevices: payload };
    },
    _setSelectedAudioSource(state, payload?: MediaDeviceInfo) {
      return { ...state, selectedAudioSource: payload };
    },
    clearSelectedAudioSource(state) {
      return { ...state, selectedAudioSource: undefined };
    },
    setVolumeDetectLevel(state, payload: VolumeDetectLevel) {
      return { ...state, volumeDetectLevel: payload };
    },
    setMeterVolume(state, payload: number) {
      return { ...state, meterVolume: payload };
    },

    setAudioStream(state, payload?: MediaStream) {
      return { ...state, audioStream: payload };
    },

    setAnalyser(state, payload?: IAnalyserNode<IAudioContext>) {
      return { ...state, analyser: payload };
    },
    setAudioSource(state, payload?: IMediaStreamAudioSourceNode<IAudioContext>) {
      return { ...state, audioSource: payload };
    },
    setVolumeInterval(state, payload?: ReturnType<typeof setInterval>) {
      return { ...state, volumeInterval: payload };
    },
    setVolumeLevelInterval(state, payload?: ReturnType<typeof setInterval>) {
      return { ...state, volumeLevelInterval: payload };
    },

    isDetectingVolume(state, payload: boolean) {
      return { ...state, isDetectingVolume: payload };
    },

    clear() {
      return initialState;
    },
  },
  effects: (dispatch) => ({
    async startVolumeDetection(disableChunkRecording?: boolean) {
      const { selectedAudioSource, isDetectingVolume } = store.getState().audio;
      if (isTauri()) {
        return;
      }
      if (USE_CHUNK_RECORDING() && !disableChunkRecording) {
        await chunkAudio.startVolumeDetection();
        return;
      }
      if (isDetectingVolume) {
        return;
      }

      dispatch.audio.stopVolumeDetection();
      dispatch.audio.isDetectingVolume(true);

      const audioStream = await navigator.mediaDevices.getUserMedia({
        video: false,

        audio: isSafari
          ? {
              deviceId: selectedAudioSource?.deviceId,
            }
          : {
              autoGainControl: false,
              echoCancellation: false,
              noiseSuppression: false,

              deviceId: selectedAudioSource?.deviceId,
            },
      });

      dispatch.audio.setAudioStream(audioStream);
      const audioContext = new AudioContext();

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.minDecibels = -127;
      analyser.maxDecibels = 0;
      analyser.smoothingTimeConstant = 0.4;

      const audioSource = audioContext.createMediaStreamSource(audioStream);
      dispatch.audio.setAudioSource(audioSource);
      audioSource.connect(analyser);

      let maxVolume = 0;

      const volumes = new Uint8Array(analyser.frequencyBinCount);
      const volumeCallback = () => {
        // if (
        //   !isEqual(selectedAudioSourceDevice, store.getState().audio.selectedAudioSource)
        // ) {
        //   analyser.disconnect();
        //   void audioContext.close();

        //   audioSource.disconnect();

        //   cleanUpCallback();
        //   return;
        // }
        analyser.getByteFrequencyData(volumes);
        let volumeSum = 0;
        for (const volume of volumes) {
          volumeSum += volume;
        }
        const averageVolume = volumeSum / volumes.length;
        // logger.debug('averageVolume', averageVolume, selectedAudioSourceDevice?.label);
        // Value range: 127 = analyser.maxDecibels - analyser.minDecibels;
        // volumeVisualizer.style.setProperty('--volume', (averageVolume * 100) / 127 + '%');

        const meterVolume = (averageVolume * 100) / 127;

        dispatch.audio.setMeterVolume(meterVolume);

        maxVolume = max([maxVolume, meterVolume]) || 0;
      };

      const volumeInterval = setInterval(volumeCallback, 200);
      dispatch.audio.setVolumeInterval(volumeInterval);

      let countDown = 0;
      const checkVolume2 = () => {
        const threshold1 = 10;
        const threshold2 = 20;

        countDown--;
        // logger.debug(
        //   'countDown',
        //   countDown,
        //   maxVolume,
        //   selectedAudioSourceDevice?.label + selectedAudioSourceDevice.deviceId,
        //   deviceId === store.getState().audio.selectedAudioSource?.deviceId
        // );

        if (countDown > 0) {
          return;
        }

        if (maxVolume < threshold1) {
          dispatch.audio.setVolumeDetectLevel('noSound');
          countDown = 3;
        } else if (maxVolume < threshold2) {
          dispatch.audio.setVolumeDetectLevel('low');
          countDown = 3;
        } else {
          dispatch.audio.setVolumeDetectLevel('normal');
          countDown = 50;
        }

        maxVolume = 0;
      };
      const volumeLevelInterval = setInterval(checkVolume2, 1000);

      dispatch.audio.setVolumeLevelInterval(volumeLevelInterval);
    },
    stopVolumeDetection(disableChunkRecording?: boolean) {
      // if (store.getState().timerModel.isRunning) {
      //   return;
      // }
      if (USE_CHUNK_RECORDING() && !disableChunkRecording) {
        chunkAudio.stopVolumeDetection();
        return;
      }

      dispatch.audio.setMeterVolume(0);
      dispatch.audio.setVolumeDetectLevel('normal');

      const { audioSource, audioStream, volumeInterval, volumeLevelInterval } =
        store.getState().audio;
      audioSource?.disconnect();

      audioStream?.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });

      dispatch.audio.setVolumeInterval(undefined);
      dispatch.audio.setVolumeLevelInterval(undefined);

      clearInterval(volumeInterval);
      clearInterval(volumeLevelInterval);

      dispatch.audio.isDetectingVolume(false);
    },

    async setSelectedAudioSource(payload?: MediaDeviceInfo) {
      if (USE_CHUNK_RECORDING()) {
        await chunkAudio.setSelectedAudioSource(payload);
        return;
      }
      logger.debug('set selected audio source');
      // // stopping
      dispatch.scribeRecordModel._stopExistingMediaRecorder();

      dispatch.audio._setSelectedAudioSource(payload);

      const { isRunning } = store.getState().timerModel;

      const { isDetectingVolume } = store.getState().audio;
      if (isDetectingVolume) {
        dispatch.audio.stopVolumeDetection();
        await sleep(500);
        await dispatch.audio.startVolumeDetection();
      }

      if (isRunning) {
        logger.debug('setSelectedAudioSource' + 'isRunning');
        void dispatch.scribeRecordModel.handleSocketOpen();
        void dispatch.scribeRecordModel.startBackupOfflineRecording();
      }
    },
    async getMicPermission() {
      if (isTauri()) {
        void dispatch.scribeRecordModel.getTauriDevices();
        return;
      }
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        if (USE_RECORDING_V2()) throw new Error();

        await navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then(() => {
            if (USE_CHUNK_RECORDING()) {
              void chunkAudio.initializeAudioDevices();
            } else {
              void dispatch.audio.initializeAudioDevices();
            }
          })
          .catch(() => {
            logger.debug('Permission Denied');
          });
      } else {
        logger.debug('Browser does not support getUserMedia');
      }
    },
    async initializeAudioDevices(disableChunkRecording?: boolean) {
      if (isTauri()) {
        void dispatch.scribeRecordModel.getTauriDevices();
        return;
      }
      if (USE_CHUNK_RECORDING() && !disableChunkRecording) {
        await chunkAudio.initializeAudioDevices();
        return;
      }
      const devices = (await navigator.mediaDevices.enumerateDevices()).filter(
        (device) => device.kind === 'audioinput'
      );

      const defaultDevice = devices.find(
        (device) => device.deviceId === 'default' && device.kind === 'audioinput'
      );

      const audioDevices: MediaDeviceInfo[] = devices.filter(
        (device) => device.kind === 'audioinput' && device.deviceId !== 'default'
      );

      logger.debug('audioDevices', audioDevices);
      dispatch.audio.clearSelectedAudioSource();

      if (audioDevices.length > 0) {
        const selectedDevice =
          audioDevices.find(
            (device) =>
              (device.deviceId === defaultDevice?.deviceId ||
                defaultDevice?.label.includes(device.label)) &&
              device.deviceId !== 'default'
          ) ||
          defaultDevice ||
          audioDevices[0];

        await dispatch.audio.setSelectedAudioSource(selectedDevice);
      }

      dispatch.audio.setAudioSourceDevices(audioDevices);
    },
  }),
});
